import { accessTokens } from '../api/accessTokenMgmt'
import { isAndroid, isIOS } from './device'
import type { MintReadMetrics } from './ordinal-local-mint-signing'

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

type Context = {
    answer_id: string
    attachment_id?: string
    signing_request_id?: string
    content_bytes?: number
    source?: string
}
export type MintStage = 'media_info' | 'intent_hash' | 'mint_request' | 'sign' | 'submit'
export function reportMintDiagnostic(
    context: Context,
    stage: MintStage,
    outcome: 'started' | 'progress' | 'completed' | 'failed',
    metrics: Partial<MintReadMetrics> & { duration_ms?: number } = {},
): void {
    try {
        if (typeof window === 'undefined') return
        const webCommit = (window as unknown as { Knov?: { version?: { gitCommitHash?: string } } })
            .Knov?.version?.gitCommitHash
        void fetch(`${window.knovApiUrl}/api/v1/ordinal_mint_diagnostics`, {
            method: 'POST',
            headers: { ...accessTokens(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ...context,
                stage,
                outcome,
                ...metrics,
                platform: isAndroid ? 'android' : isIOS ? 'ios' : 'web',
                web_commit: webCommit,
            }),
            keepalive: true,
        }).catch(() => {})
    } catch {
        /* Diagnostics must never affect minting. */
    }
}
export async function measureMintStage<T>(
    context: Context,
    stage: MintStage,
    run: (observe: (metrics: MintReadMetrics) => void) => Promise<T>,
    succeeded: (result: T) => boolean = () => true,
): Promise<T> {
    const start = now()
    let metrics: Partial<MintReadMetrics> = {}
    reportMintDiagnostic(context, stage, 'started')
    try {
        const result = await run(value => {
            metrics = value
            reportMintDiagnostic(context, stage, 'progress', metrics)
        })
        reportMintDiagnostic(context, stage, succeeded(result) ? 'completed' : 'failed', {
            ...metrics,
            duration_ms: now() - start,
        })
        return result
    } catch (error) {
        reportMintDiagnostic(context, stage, 'failed', { ...metrics, duration_ms: now() - start })
        throw error
    }
}
